import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import licenseService from '../../../services/licenseService';
import { useUser } from '../../authentication';
import { CurriculumPricing, getCurriculumPricing } from '../../curriculum';
import { License, LicenseSubscription } from '../models/license';

export type LicenseWithCurriculum = License & {
  curriculum?: CurriculumWithSubscription;
};

export type CurriculumWithSubscription = {
  brand: { code: string; name: string };
  subscription?: LicenseSubscription;
  name?: string;
};

const addCurriculumToLicenses = (licenses: License[], curriculum: CurriculumPricing[]): LicenseWithCurriculum[] =>
  licenses.map(license => ({
    ...license,
    curriculum: licenseService.getCorrespondingCurriculum(license, curriculum),
  }));

const useFetchSubscriptions = () => {
  const { user } = useUser();
  const [data, setData] = useState<{
    isLoading: boolean;
    isError?: boolean;
    isAccessDenied?: boolean;
    allLicenses?: LicenseWithCurriculum[];
    upcomingLicenses?: LicenseWithCurriculum[];
    activeLicenses?: LicenseWithCurriculum[];
    expiredLicenses?: LicenseWithCurriculum[];
    curriculum?: CurriculumPricing[];
  }>({
    isLoading: true,
    isError: false,
    isAccessDenied: false,
    allLicenses: undefined,
    upcomingLicenses: undefined,
    activeLicenses: undefined,
    expiredLicenses: undefined,
    curriculum: undefined,
  });

  const setErrorState = () => setData({ isLoading: false, isError: true });
  const setUnauthorizedState = () => setData({ isLoading: false, isAccessDenied: true });

  const combineSubscriptionLicenses = (licenses: License[]): License[] =>
    licenses.reduceRight(
      (acc, cur) =>
        acc.find(a => a.id === cur.id || (cur.subscription?.id && a.subscription?.id === cur.subscription?.id))
          ? acc
          : [...acc, cur],
      [] as License[]
    );

  const sortLicensesByStartDate = (licenses: License[]): License[] =>
    [...licenses].sort((l1, l2) => parseISO(l2.startDate).getTime() - parseISO(l1.startDate).getTime());

  const separateActiveAndExpiredLicenses = (licenses: License[]) =>
    licenses.reduce(
      ({ active, expired, upcoming }, license) => {
        return licenseService.isFutureSubscription(license)
          ? { active: active, expired: expired, upcoming: [...upcoming, license] }
          : license.expired === true
          ? { active: active, expired: [...expired, license], upcoming: upcoming }
          : { active: [...active, license], expired, upcoming };
      },
      { active: [] as License[], expired: [] as License[], upcoming: [] as License[] }
    );

  useEffect(() => {
    if (!user?.permissions?.includes('Account View')) {
      return setUnauthorizedState();
    }

    Promise.all([licenseService.getPairedLicenses(user?.lastSelectedAccount), getCurriculumPricing()])
      .then(([pairedLicenses, curriculum]) => {
        const { active, expired, upcoming } = separateActiveAndExpiredLicenses(
          sortLicensesByStartDate(combineSubscriptionLicenses(addCurriculumToLicenses(pairedLicenses, curriculum)))
        );

        setData(prev => ({
          ...prev,
          isLoading: false,
          allLicenses: addCurriculumToLicenses(pairedLicenses, curriculum),
          upcomingLicenses: upcoming,
          activeLicenses: active,
          expiredLicenses: expired,
          curriculum: curriculum,
        }));
      })
      .catch(reason => {
        console.error(reason);
        setErrorState();
      });
  }, []);

  return data;
};

export default useFetchSubscriptions;
